import React, { useState, memo, useContext, useMemo } from 'react'
import styled, { ThemeContext } from 'styled-components'
import {
  Cell,
  PieChart,
  Pie,
  ResponsiveContainer,
  Tooltip,
  LabelList
} from 'recharts'
import numeral from 'numeral'
import {
  removeSmallData,
  renderCustomPieChartLabel,
  renderCustomPieChartLabelLine
} from '../util'
import { PieLabelArray } from './PieLabelArray'
import {
  renderWhitePiePercentLabel,
  renderWhitePieLabelLine
} from '../util/pieChartLabel'

import StatsCard from './StatsCard'
import MotivationalMessage from './MotivationalMessage'

const containerStyles = `
  grid-row-start: 3;
  grid-row-end: span 2;

  @media (max-width: 700px) {
    grid-row-start: 5;
    grid-row-end: span 2;
  }
`

const formatTooltipLabel = (value, name, props) => [
  numeral(value).format('$0,0[.]00'),
  name
]

// Custom label function for Recharts 2.x
const PiePercentLabel = ({ percent, x, y }) => {
  if (percent < 0.03) return null
  return (
    <text
      x={x}
      y={y}
      fontSize={14}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  )
}

export const DistributionOfRevenue = memo(({ stats, tooltipText }) => {
  const theme = useContext(ThemeContext)
  const bluePalette = theme.dashboard.chartSalesGradient || []

  const sortedData = useMemo(() => {
    let data = []
    if (stats) {
      data = Object.keys(stats).map(key => {
        const value = stats[key] || 0
        return { name: key, value }
      })
      data.sort((a, b) => b.value - a.value)
    }
    return data
  }, [stats])

  // Create a formatter for the LabelList
  const formatPieLabel = entry => {
    if (entry.percent < 0.03) return '' // Hide small slices
    return `${(entry.percent * 100).toFixed(0)}%`
  }

  const shortLabelsLength = 3

  return (
    <StatsCard
      title={'Distribution of Revenue'}
      containerStyles={containerStyles}
      tooltipText={tooltipText}
    >
      {sortedData.length ? (
        <>
          <div style={{ width: '100%' }}>
            <ResponsiveContainer height={200} width="100%">
              <PieChart>
                <Pie
                  dataKey="value"
                  data={sortedData}
                  cx="50%"
                  cy="50%"
                  innerRadius={5}
                  outerRadius={50}
                  label={renderWhitePiePercentLabel}
                  labelLine={renderWhitePieLabelLine}
                >
                  {sortedData.map((entry, index) => {
                    const colorIndex = index % bluePalette.length
                    const fillColor = bluePalette[colorIndex]
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={fillColor}
                        stroke={fillColor}
                        strokeWidth={1}
                      />
                    )
                  })}
                </Pie>
                <Tooltip formatter={formatTooltipLabel} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <PieLabelArray
            elementId="dist-of-rev-labels"
            data={sortedData}
            shortenTo={shortLabelsLength}
            colorPalette={bluePalette}
          />
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <MotivationalMessage message={'Nothing yet, go get those sales!'} />
        </div>
      )}
    </StatsCard>
  )
})
